import { CallingState, useCall, useCallStateHooks, StreamTheme } from '@stream-io/video-react-sdk';
import { useNavigate } from '@tanstack/react-router';
import { ParticipantList } from './participant-list';
import { FloatingLocalParticipant } from './floating-local-participant';
import { useContext, useEffect } from 'react';
import { ThemeContext } from '@/context/theme-context';
import { LoaderCircle } from 'lucide-react';
import '@stream-io/video-react-sdk/dist/css/styles.css';
import './custom-styles.css';
import { CallControls } from './call-controls';
import { SquaredBackground } from '@/components/squared-background';
import { useVideoCallLogger } from '@/hooks/useVideoCallLogger';
import { reportError } from '@/utils/errorReporter';
import { useTranslation } from 'react-i18next';

export const InprogressCall = () => {
  const call = useCall();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const logger = useVideoCallLogger();

  const { t } = useTranslation();

  const { useCallCallingState, useRemoteParticipants, useLocalParticipant } = useCallStateHooks();

  const callingState = useCallCallingState();
  const remoteParticipants = useRemoteParticipants();
  const localParticipant = useLocalParticipant();

  if (callingState == CallingState.LEFT || callingState == CallingState.UNKNOWN) {
    navigate({ to: '/auth/chat' });
  }

  const { useCameraState, useMicrophoneState, useCallSettings } = useCallStateHooks();
  const cameraState = useCameraState();
  const microphoneState = useMicrophoneState();
  const callSettings = useCallSettings();

  useEffect(() => {
    // Skip logging entirely if not in proper call state or missing essential state objects
    if (
      callingState !== CallingState.JOINED ||
      !call ||
      !cameraState ||
      !microphoneState ||
      !callSettings
    ) {
      return;
    }

    try {
      logger.logCallStateJoined({
        camera_muted: cameraState?.optimisticIsMute,
        microphone_muted: microphoneState?.optimisticIsMute,
        camera_status: cameraState?.status,
        microphone_status: microphoneState?.status,
        camera_browser_permission: cameraState?.hasBrowserPermission,
        microphone_browser_permission: microphoneState?.hasBrowserPermission,
        video_enabled: callSettings?.video?.enabled,
        audio_enabled: !!callSettings?.audio,
        call_id: call?.id,
        call_type: call?.type,
        participant_count: remoteParticipants?.length ? remoteParticipants.length + 1 : 1,
      });
    } catch (error) {
      reportError(error);
    }
  }, [callingState, call?.id]);

  useEffect(() => {
    // Skip logging entirely if not in proper call state or missing essential state objects
    if (!call?.id || !cameraState || !microphoneState) {
      return;
    }

    try {
      logger.logParticipantStateChange({
        participant_count: remoteParticipants?.length ? remoteParticipants.length + 1 : 1,
        camera_muted: cameraState?.optimisticIsMute,
        microphone_muted: microphoneState?.optimisticIsMute,
        camera_status: cameraState?.status,
        microphone_status: microphoneState?.status,
        call_id: call?.id,
        call_type: call?.type,
      });
    } catch (error) {
      reportError(error);
    }
  }, [remoteParticipants?.length, call?.id]);

  if (callingState !== CallingState.JOINED || !call) {
    return (
      <div className="min-h-screen min-w-screen flex flex-col space-y-4 items-center justify-center text-4xl font-bold bg-background text-foreground relative isolate">
        <SquaredBackground />

        <div className="w-12 h-12">
          <LoaderCircle className="w-12 h-12 animate-spin" />
        </div>

        <p>{t('video_call.joiningCall')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full items-center justify-center relative isolate bg-background">
      <SquaredBackground />

      <StreamTheme className={theme}>
        <div className="pt-10 px-4 lg:px-0">
          {remoteParticipants.length == 0 ? (
            <section className="w-full min-h-[80vh] flex items-center justify-center text-xl text-center">
              <div className="flex flex-col items-center justify-center space-y-4">
                <h1 className="text-2xl lg:text-4xl font-bold">
                  {t('video_call.waitingForPsychologistTitle')}
                </h1>
                <section className="text-sm lg:text-base text-center">
                  <p>{t('video_call.waitingForPsychologistDescription')}</p>
                  <p>{t('video_call.waitingForPsychologistNote')}</p>
                </section>
              </div>
            </section>
          ) : (
            <ParticipantList participants={remoteParticipants} />
          )}
        </div>

        <FloatingLocalParticipant participant={localParticipant} />

        <div className="pt-8">
          <CallControls />
        </div>
      </StreamTheme>
    </div>
  );
};
